import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { useTheme } from '@emotion/react';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import { useDispatch } from 'react-redux';
import { useActiveTab } from 'reducers/viewsSlice';
import { checkCustomRenameLocationToSubsidiaryEnabled } from 'utils/helpers/featureFlags';

import { Search } from 'assets/assetsV1/iconComponents';

import { VIEWS_FILTER_KEYS, VIEWS_FILTER_KEYS_DISPLAY_NAMES } from 'constants/ProjectConstants';
import { sendSearchValueEvent } from 'constants/mixpanelHelpers/viewsEvents';

import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';
import { ChakraStyleInput } from 'uiToolkitV1/InputV2/ChakraStyleInput';
import { Select } from 'uiToolkitV1/Select';

export const AgGridContainer = ({
  columnDefs,
  rowData,
  checkedRows,
  setCheckedRows,
  sortColumnId,
  treeData,
  getDataPath,
  autoGroupColumnDef,
  onRowClicked,
  ToolBarRightComponent,
  rowHeight = undefined,
  rowCheckedTesterFn,
}) => {
  const dispatch = useDispatch();
  const gridRef = useRef();
  const [sortDir, setSortDir] = useState('asc');
  const [searchText, setSearchText] = useState('');
  const theme = useTheme();

  const defaultColDef = useMemo(
    () => ({
      autoHeight: true,
      flex: 1,
    }),
    []
  );

  const gridStyle = useMemo(
    () => ({
      height: 'calc(100% - 104px)',
      width: '100%',
      padding: '0 8px',
      borderTop: 'var(--ag-row-border-style) var(--ag-row-border-color) var(--ag-row-border-width)',
    }),
    []
  );

  const markDefaultCheck = useCallback(() => {
    gridRef.current?.api?.forEachNode((node) => {
      const checked = rowCheckedTesterFn({ nodeData: node.data, checkedRows });
      node.setSelected(!!checked);
    });
  }, [checkedRows, rowCheckedTesterFn]);

  useEffect(() => {
    markDefaultCheck();
  }, [rowData, checkedRows, rowCheckedTesterFn, markDefaultCheck]);

  const sortAsc = useCallback(() => {
    gridRef.current?.api?.applyColumnState({
      state: [{ colId: sortColumnId, sort: 'asc' }],
      defaultState: { sort: null },
    });
  }, [sortColumnId]);

  const sortDesc = useCallback(() => {
    gridRef.current?.api?.applyColumnState({
      state: [{ colId: sortColumnId, sort: 'desc' }],
      defaultState: { sort: null },
    });
  }, [sortColumnId]);

  const sort = useCallback(
    (value) => {
      if (value === 'asc') {
        sortAsc();
      } else {
        sortDesc();
      }
    },
    [sortAsc, sortDesc]
  );

  const onSelectionChanged = useCallback(
    (event) => {
      if (event.source === 'api') return;
      dispatch(setCheckedRows(event.api.getSelectedRows()));
      sort(sortDir);
    },
    [dispatch, setCheckedRows, sort, sortDir]
  );

  const sortingOptions = useMemo(
    () => [
      {
        label: 'A to Z',
        value: 'asc',
      },
      {
        label: 'Z to A',
        value: 'desc',
      },
    ],
    []
  );

  const locationFilterInputPlaceholder = `Search ${
    checkCustomRenameLocationToSubsidiaryEnabled() ? 'Subsidiaries' : 'Locations'
  }`;

  const filterInputPlaceholder = `Search ${VIEWS_FILTER_KEYS_DISPLAY_NAMES[sortColumnId]}${
    VIEWS_FILTER_KEYS.ETHNICITY === sortColumnId ? '' : 's'
  }`;

  const filterType = useActiveTab();
  const page = useViewPageName();

  const searchTimeoutRef = useRef(null);
  const onChangeWithEvent = (e) => {
    const newSearchText = e.target.value;
    setSearchText(newSearchText);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      // ignore if searchText is empty
      if (newSearchText) {
        sendSearchValueEvent({ eventProperties: { page, filterType, searchText: newSearchText } });
      }
    }, 2000);
  };

  return (
    <>
      <Flex alignItems="center" padding="10px">
        <ChakraStyleInput
          leftComponent={<Search />}
          placeholder={sortColumnId === 'location' ? locationFilterInputPlaceholder : filterInputPlaceholder}
          value={searchText}
          onChange={onChangeWithEvent}
        />
        <label style={{ color: theme.colors.text.g3, display: 'flex', marginLeft: '10px' }}>
          <span style={{ minWidth: 'max-content' }}>Sort by:</span>
          <Select
            handleChange={({ value }) => {
              sort(value);
              setSortDir(value);
            }}
            options={sortingOptions}
            isSearchable={false}
            withBackground={false}
            formControlProps={{
              minWidth: '75px',
            }}
            minWidth="max-content"
            selectProps={{
              defaultValue: sortingOptions[0],
            }}
          />
          {/* <StyledSelect
            variant="unstyled"
            onChange={(e) => sort(e.target.value)}
            defaultValue={'none'}
          >
            {sortingOptions.map((o) => {
              return <option value={o.value}>{o.label}</option>;
            })}
          </StyledSelect> */}
        </label>
        {ToolBarRightComponent && <ToolBarRightComponent />}
      </Flex>
      <div style={gridStyle} className="ag-theme-alpine">
        <AgGridWrapper
          /* This component is not allowing header so no filter / column persistence */
          useFilterModelPersistence={false}
          useColumnStatePersistence={false}
          gridStatePersistenceKey={null}
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          animateRows
          rowSelection={{
            enableSelectionWithoutKeys: true,
            mode: 'multiRow',
            enableClickSelection: true,
          }}
          onSelectionChanged={onSelectionChanged}
          headerHeight={0}
          cacheQuickFilter
          quickFilterText={searchText}
          defaultColDef={defaultColDef}
          treeData={treeData}
          getDataPath={getDataPath}
          autoGroupColumnDef={autoGroupColumnDef}
          onRowClicked={onRowClicked}
          rowHeight={rowHeight}
          onRowDataUpdated={() => sort('asc')}
          onGridReady={markDefaultCheck}
        />
      </div>
    </>
  );
};
